import React, { useMemo } from 'react';
import { computeMatchRows, computeTeamMatchRows, computePerMinRow, findMatchPosition, f0, f1, fPct, fmtDate } from './matchChronicShared';
import { computeMatchPerformanceRows } from './matchPerformanceShared';
import { generateConclusions } from './reportConclusions';

// Presentational-only — renders all 5 sections of the Full Physical Report for one player.
// Not a route: mounted off-screen by PhysicalReportExportModal.js, which captures each
// `data-report-block` div SEPARATELY with html2canvas and packs them onto PDF pages itself
// (see that file's pagination logic) — never shown on screen directly. Visual language matches
// MatchChronicPrint.js's existing PlayerPage (Inter font, #111827 text, blue accent, Heracles
// crest header) for consistency with the report the coach already knows.
//
// Every top-level piece of content is wrapped in <Block>, which stamps data-report-block +
// data-force-page attributes the modal reads to decide pagination: forceNewPage=true starts a
// fresh page (used once per section, 1-4), and each block is treated as atomic — never split
// across a page boundary — so no table ever gets cut mid-row. Section 5 is NOT forceNewPage, so
// it packs right after Section 4's conclusions when it fits, per instruction.

const CONTENT_W = 740; // block width in CSS px; PDF page margins are applied at placement time

const CLR = {
  text: '#111827', muted: '#6b7280', dimmed: '#9ca3af', border: '#e5e7eb', accent: '#2563eb',
};
const GREEN = { background: 'rgba(16,185,129,0.15)', color: '#065f46' };

const TH  = { fontSize: 8, fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase', color: CLR.muted, padding: '5px 6px', textAlign: 'right', borderBottom: `1px solid ${CLR.border}`, background: '#f9fafb', whiteSpace: 'nowrap' };
const THL = { ...TH, textAlign: 'left' };
const TD_ = { fontSize: 9, color: CLR.text, textAlign: 'right', padding: '4px 6px', borderBottom: '1px solid #f3f4f6' };
const TDL = { ...TD_, textAlign: 'left' };

function Block({ forceNewPage = false, children }) {
  return (
    <div data-report-block="true" data-force-page={forceNewPage ? 'true' : 'false'}
      style={{ width: CONTENT_W, boxSizing: 'border-box', background: '#ffffff', fontFamily: 'Inter, Arial, sans-serif', color: CLR.text }}>
      {children}
    </div>
  );
}

function SectionHeader({ label }) {
  return (
    <div style={{ fontSize: 12, fontWeight: 700, color: CLR.text, margin: '0 0 8px', paddingBottom: 4, borderBottom: `2px solid ${CLR.accent}` }}>
      {label}
    </div>
  );
}

function ReportMasthead({ player }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: `3px solid ${CLR.accent}`, paddingBottom: 10, marginBottom: 14 }}>
      <div>
        <div style={{ fontSize: 10, fontWeight: 700, color: CLR.dimmed, textTransform: 'uppercase', letterSpacing: '0.12em' }}>Full Physical Report</div>
        <div style={{ fontSize: 22, fontWeight: 800, color: CLR.text, marginTop: 2 }}>{player?.name || '—'}</div>
        {player?.position && (
          <span style={{ fontSize: 9, fontWeight: 700, padding: '2px 8px', borderRadius: 99, background: '#dbeafe', color: '#1d4ed8', display: 'inline-block', marginTop: 6 }}>
            {player.position}
          </span>
        )}
      </div>
      <img src={`${process.env.PUBLIC_URL}/heracles-crest.png`} alt="Heracles Almelo" style={{ height: 46, width: 'auto' }} />
    </div>
  );
}

// ── Pure CSS bar chart (HID + Explosive stack) — same pattern as MatchChronicPrint.js's
// PrintBarChart, reused here for Sections 1 & 2.
function MiniBarChart({ rows }) {
  if (!rows.length) return null;
  const CHART_H = 80;
  const ordered = [...rows].reverse(); // oldest first = left
  const maxVal = Math.max(1, ...ordered.map(r => (r.hid || 0) + (r.expl || 0)));
  return (
    <div style={{ width: '100%', marginBottom: 10 }}>
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 3, height: CHART_H, borderBottom: '1.5px solid #d1d5db' }}>
        {ordered.map((r, i) => {
          const total = (r.hid || 0) + (r.expl || 0);
          const barH  = total > 0 ? Math.max(3, Math.round((total / maxVal) * CHART_H)) : 3;
          const explH = total > 0 ? Math.round((r.expl / total) * barH) : 0;
          const hidH  = barH - explH;
          return (
            <div key={i} style={{ flex: 1, display: 'flex', alignItems: 'flex-end', justifyContent: 'center', height: CHART_H }}>
              <div style={{ width: '80%', maxWidth: 36, height: barH, display: 'flex', flexDirection: 'column', borderRadius: '2px 2px 0 0', overflow: 'hidden' }}>
                <div style={{ height: explH, background: '#1e3a8a', flexShrink: 0 }} />
                <div style={{ height: hidH, background: '#93c5fd', flexShrink: 0 }} />
              </div>
            </div>
          );
        })}
      </div>
      <div style={{ display: 'flex', gap: 3, marginTop: 4 }}>
        {ordered.map((r, i) => (
          <div key={i} style={{ flex: 1, textAlign: 'center', overflow: 'hidden' }}>
            <div style={{ fontSize: 7, color: '#374151', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{(r.opponent || '').split(' ')[0]}</div>
            <div style={{ fontSize: 6, color: '#9ca3af' }}>{r.date ? `${r.date.slice(8)}-${r.date.slice(5, 7)}` : ''}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

// Volume table with %-of-positional-target columns and green highlighting (>=100% of target)
// — same convention as the live MatchChronic.js / MatchChronicPrint.js tables.
function VolumeTable({ rows }) {
  const n = rows.length;
  const avg = key => n > 0 ? rows.reduce((s, r) => s + (r[key] || 0), 0) / n : null;
  const avgPct = key => {
    const vals = rows.map(r => r[key]).filter(v => v != null);
    return vals.length ? vals.reduce((s, v) => s + v, 0) / vals.length : null;
  };
  const avgRow = n > 0 ? {
    td: avg('td'), hid: avg('hid'), sd: avg('sd'), hmld: avg('hmld'), sa: avg('sa'), maxSpd: avg('maxSpd'), mins: avg('mins'),
    tdPct: avgPct('tdPct'), hidPct: avgPct('hidPct'), sdPct: avgPct('sdPct'), hmldPct: avgPct('hmldPct'), saPct: avgPct('saPct'),
  } : null;
  return (
    <>
      <div style={{ fontSize: 10, fontWeight: 700, color: CLR.text, margin: '8px 0 4px' }}>Volume</div>
      <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 10 }}>
        <thead><tr>
          <th style={THL}>Opponent</th><th style={TH}>Date</th>
          <th style={TH}>TD</th><th style={TH}>TD%</th>
          <th style={TH}>HID</th><th style={TH}>HID%</th>
          <th style={TH}>SD</th><th style={TH}>SD%</th>
          <th style={TH}>HMLD</th><th style={TH}>HMLD%</th>
          <th style={TH}>SA</th><th style={TH}>SA%</th>
          <th style={TH}>Max Spd</th><th style={TH}>Mins</th>
        </tr></thead>
        <tbody>
          {rows.map(r => (
            <tr key={r.sessionId}>
              <td style={TDL}>{r.opponent}</td><td style={TD_}>{fmtDate(r.date)}</td>
              <td style={{ ...TD_, ...(r.tdGreen ? GREEN : {}) }}>{f0(r.td)}</td>
              <td style={{ ...TD_, ...(r.tdGreen ? GREEN : {}) }}>{fPct(r.tdPct)}</td>
              <td style={{ ...TD_, ...(r.hidGreen ? GREEN : {}) }}>{f0(r.hid)}</td>
              <td style={{ ...TD_, ...(r.hidGreen ? GREEN : {}) }}>{fPct(r.hidPct)}</td>
              <td style={{ ...TD_, ...(r.sdGreen ? GREEN : {}) }}>{f0(r.sd)}</td>
              <td style={{ ...TD_, ...(r.sdGreen ? GREEN : {}) }}>{fPct(r.sdPct)}</td>
              <td style={{ ...TD_, ...(r.hmldGreen ? GREEN : {}) }}>{f0(r.hmld)}</td>
              <td style={{ ...TD_, ...(r.hmldGreen ? GREEN : {}) }}>{fPct(r.hmldPct)}</td>
              <td style={{ ...TD_, ...(r.saGreen ? GREEN : {}) }}>{f0(r.sa)}</td>
              <td style={{ ...TD_, ...(r.saGreen ? GREEN : {}) }}>{fPct(r.saPct)}</td>
              <td style={TD_}>{f1(r.maxSpd)}</td><td style={TD_}>{f1(r.mins)}</td>
            </tr>
          ))}
          {avgRow && (
            <tr style={{ background: 'rgba(37,99,235,0.05)', borderTop: `2px solid ${CLR.border}` }}>
              <td style={{ ...TDL, fontWeight: 700 }}>Average</td><td style={TD_}>—</td>
              <td style={{ ...TD_, fontWeight: 700 }}>{f0(avgRow.td)}</td><td style={{ ...TD_, fontWeight: 700 }}>{fPct(avgRow.tdPct)}</td>
              <td style={{ ...TD_, fontWeight: 700 }}>{f0(avgRow.hid)}</td><td style={{ ...TD_, fontWeight: 700 }}>{fPct(avgRow.hidPct)}</td>
              <td style={{ ...TD_, fontWeight: 700 }}>{f0(avgRow.sd)}</td><td style={{ ...TD_, fontWeight: 700 }}>{fPct(avgRow.sdPct)}</td>
              <td style={{ ...TD_, fontWeight: 700 }}>{f0(avgRow.hmld)}</td><td style={{ ...TD_, fontWeight: 700 }}>{fPct(avgRow.hmldPct)}</td>
              <td style={{ ...TD_, fontWeight: 700 }}>{f0(avgRow.sa)}</td><td style={{ ...TD_, fontWeight: 700 }}>{fPct(avgRow.saPct)}</td>
              <td style={{ ...TD_, fontWeight: 700 }}>{f1(avgRow.maxSpd)}</td><td style={{ ...TD_, fontWeight: 700 }}>{f1(avgRow.mins)}</td>
            </tr>
          )}
        </tbody>
      </table>
    </>
  );
}

function PerMinTable({ rows }) {
  const perMinRow = computePerMinRow(rows);
  if (!perMinRow) return null;
  return (
    <>
      <div style={{ fontSize: 10, fontWeight: 700, color: CLR.text, margin: '8px 0 4px' }}>Per Minute</div>
      <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 10 }}>
        <thead><tr>
          <th style={THL}>Opponent</th><th style={TH}>Date</th>
          <th style={TH}>TD/Min</th><th style={TH}>HID/Min</th><th style={TH}>SD/Min</th><th style={TH}>HMLD/Min</th><th style={TH}>SA/Min</th>
        </tr></thead>
        <tbody>
          {rows.map(r => {
            const pm = r.mins > 0 ? { td: r.td / r.mins, hid: r.hid / r.mins, sd: r.sd / r.mins, hmld: r.hmld / r.mins, sa: r.sa / r.mins } : null;
            return (
              <tr key={r.sessionId}>
                <td style={TDL}>{r.opponent}</td><td style={TD_}>{fmtDate(r.date)}</td>
                <td style={TD_}>{pm ? f0(pm.td) : '—'}</td>
                <td style={TD_}>{pm ? f1(pm.hid) : '—'}</td>
                <td style={TD_}>{pm ? f1(pm.sd) : '—'}</td>
                <td style={TD_}>{pm ? f1(pm.hmld) : '—'}</td>
                <td style={TD_}>{pm ? f1(pm.sa) : '—'}</td>
              </tr>
            );
          })}
          <tr style={{ background: 'rgba(37,99,235,0.05)', borderTop: `2px solid ${CLR.border}` }}>
            <td style={{ ...TDL, fontWeight: 700 }}>Average / Min Played</td><td style={TD_}>—</td>
            <td style={{ ...TD_, fontWeight: 700 }}>{f0(perMinRow.td)}</td>
            <td style={{ ...TD_, fontWeight: 700 }}>{f1(perMinRow.hid)}</td>
            <td style={{ ...TD_, fontWeight: 700 }}>{f1(perMinRow.sd)}</td>
            <td style={{ ...TD_, fontWeight: 700 }}>{f1(perMinRow.hmld)}</td>
            <td style={{ ...TD_, fontWeight: 700 }}>{f1(perMinRow.sa)}</td>
          </tr>
        </tbody>
      </table>
    </>
  );
}

function PerformanceVolumeTable({ rows }) {
  return (
    <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 6 }}>
      <thead><tr>
        <th style={THL}>Player</th><th style={TH}>Pos</th>
        <th style={TH}>TD</th><th style={TH}>TD%</th>
        <th style={TH}>HID</th><th style={TH}>HID%</th>
        <th style={TH}>SD</th><th style={TH}>SD%</th>
        <th style={TH}>HMLD</th><th style={TH}>HMLD%</th>
        <th style={TH}>SA</th><th style={TH}>SA%</th>
        <th style={TH}>Max Spd</th><th style={TH}>Mins</th>
      </tr></thead>
      <tbody>
        {rows.map(r => (
          <tr key={r.name}>
            <td style={TDL}>{r.name}</td><td style={TD_}>{r.pos || '—'}</td>
            <td style={{ ...TD_, ...(r.tdGreen ? GREEN : {}) }}>{f0(r.td)}</td>
            <td style={{ ...TD_, ...(r.tdGreen ? GREEN : {}) }}>{fPct(r.tdPct)}</td>
            <td style={{ ...TD_, ...(r.hidGreen ? GREEN : {}) }}>{f0(r.hid)}</td>
            <td style={{ ...TD_, ...(r.hidGreen ? GREEN : {}) }}>{fPct(r.hidPct)}</td>
            <td style={{ ...TD_, ...(r.sdGreen ? GREEN : {}) }}>{f0(r.sd)}</td>
            <td style={{ ...TD_, ...(r.sdGreen ? GREEN : {}) }}>{fPct(r.sdPct)}</td>
            <td style={{ ...TD_, ...(r.hmldGreen ? GREEN : {}) }}>{f0(r.hmld)}</td>
            <td style={{ ...TD_, ...(r.hmldGreen ? GREEN : {}) }}>{fPct(r.hmldPct)}</td>
            <td style={{ ...TD_, ...(r.saGreen ? GREEN : {}) }}>{f0(r.sa)}</td>
            <td style={{ ...TD_, ...(r.saGreen ? GREEN : {}) }}>{fPct(r.saPct)}</td>
            <td style={TD_}>{f1(r.maxSpd)}</td><td style={TD_}>{f1(r.mins)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function PerformancePerMinTable({ rows }) {
  return (
    <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 10 }}>
      <thead><tr>
        <th style={THL}>Player</th><th style={TH}>Pos</th>
        <th style={TH}>TD/Min</th><th style={TH}>HID/Min</th><th style={TH}>SD/Min</th><th style={TH}>HMLD/Min</th><th style={TH}>SA/Min</th>
      </tr></thead>
      <tbody>
        {rows.map(r => (
          <tr key={r.name}>
            <td style={TDL}>{r.name}</td><td style={TD_}>{r.pos || '—'}</td>
            <td style={TD_}>{r.tdPerMin != null ? f0(r.tdPerMin) : '—'}</td>
            <td style={TD_}>{r.hidPerMin != null ? f1(r.hidPerMin) : '—'}</td>
            <td style={TD_}>{r.sdPerMin != null ? f1(r.sdPerMin) : '—'}</td>
            <td style={TD_}>{r.hmldPerMin != null ? f1(r.hmldPerMin) : '—'}</td>
            <td style={TD_}>{r.saPerMin != null ? f1(r.saPerMin) : '—'}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// Where this player's Physical score sits versus the rest of the (non-null) squad — a simple
// percentile-rank quartile, not a statistical model. Needs a handful of squad scores to be
// meaningful; below that it's omitted rather than shown as a misleading "quartile of 2".
function computeQuartileContext(physicalScore, squadScores) {
  const scores = (squadScores || []).filter(v => v != null && !isNaN(v));
  if (physicalScore == null || scores.length < 4) return null;
  const sorted = [...scores].sort((a, b) => a - b);
  const atOrBelow = sorted.filter(v => v <= physicalScore).length;
  const percentile = Math.round((atOrBelow / sorted.length) * 100);
  const quartileLabel = percentile > 75 ? 'top quartile' : percentile > 50 ? '2nd quartile' : percentile > 25 ? '3rd quartile' : 'bottom quartile';
  return { percentile, quartileLabel, squadSize: sorted.length };
}

export default function PhysicalReportContent({
  player, players, opponents, scenarios, customTemplates, positions, allMatchDrills, matchIds, physicalScore, squadPhysicalScores,
}) {
  const matchIdSet = useMemo(() => new Set((matchIds || []).map(String)), [matchIds]);

  const teamRowsAll = useMemo(
    () => computeTeamMatchRows(allMatchDrills, players, opponents, scenarios, customTemplates, positions),
    [allMatchDrills, players, opponents, scenarios, customTemplates, positions]);
  const teamRows = useMemo(
    () => teamRowsAll.filter(r => matchIdSet.size === 0 || matchIdSet.has(String(r.sessionId))),
    [teamRowsAll, matchIdSet]);

  const playerDrills = useMemo(() => {
    if (!player?.name) return [];
    const nameLower = player.name.toLowerCase();
    return (allMatchDrills || []).filter(d => (d.player_name || '').toLowerCase() === nameLower);
  }, [allMatchDrills, player]);
  const playerRowsAll = useMemo(
    () => computeMatchRows(playerDrills, player, opponents, scenarios, customTemplates, positions),
    [playerDrills, player, opponents, scenarios, customTemplates, positions]);
  const playerRows = useMemo(
    () => playerRowsAll.filter(r => matchIdSet.size === 0 || matchIdSet.has(String(r.sessionId))),
    [playerRowsAll, matchIdSet]);

  // Section 3: one Match Performance table pair (volume + per-minute) per selected match,
  // filtered to the position the report player actually played THAT match (via
  // findMatchPosition — a player who moved between positions during the season, e.g. CB in
  // some matches and CDM in others, gets compared against the right position each time, not
  // always his current players.position).
  const performanceByMatch = useMemo(() => {
    const ids = matchIdSet.size > 0 ? [...matchIdSet] : teamRowsAll.map(r => String(r.sessionId));
    return ids.map(id => {
      const meta = teamRowsAll.find(r => String(r.sessionId) === id);
      const drillsForSession = (allMatchDrills || []).filter(d => String(d.session_id) === id);
      const matchDate = meta?.date;
      const playerPosThatMatch = findMatchPosition(positions, player?.id, matchDate, player?.position);
      const rows = computeMatchPerformanceRows(drillsForSession, players, matchDate, scenarios, customTemplates, positions)
        .filter(r => !playerPosThatMatch || r.pos === playerPosThatMatch);
      return { sessionId: id, opponent: meta?.opponent || '—', date: matchDate, position: playerPosThatMatch, rows };
    }).filter(m => m.rows.length > 0)
      .sort((a, b) => (b.date || '').localeCompare(a.date || ''));
  }, [matchIdSet, teamRowsAll, allMatchDrills, players, scenarios, customTemplates, positions, player]);

  const conclusions = useMemo(
    () => generateConclusions({ playerRows, squadRows: teamRows }),
    [playerRows, teamRows]);

  const quartile = useMemo(
    () => computeQuartileContext(physicalScore, squadPhysicalScores),
    [physicalScore, squadPhysicalScores]);

  return (
    <>
      {/* ── Section 1: Match Chronic — Team ── */}
      <Block forceNewPage>
        <ReportMasthead player={player} />
        <SectionHeader label="1. Match Chronic — Team" />
        <MiniBarChart rows={teamRows} />
      </Block>
      <Block><VolumeTable rows={teamRows} /></Block>
      <Block><PerMinTable rows={teamRows} /></Block>

      {/* ── Section 2: Match Chronic — Player ── */}
      <Block forceNewPage>
        <SectionHeader label={`2. Match Chronic — ${player?.name || 'Player'}`} />
        <MiniBarChart rows={playerRows} />
      </Block>
      <Block><VolumeTable rows={playerRows} /></Block>
      <Block><PerMinTable rows={playerRows} /></Block>

      {/* ── Section 3: Match Performance, one pair of tables per selected match — the position
          compared against is resolved per match (see performanceByMatch above), since it can
          differ from one match to the next. ── */}
      <Block forceNewPage>
        <SectionHeader label="3. Match Performance — vs positional teammates" />
        {performanceByMatch.length === 0 && (
          <div style={{ fontSize: 10, color: CLR.muted }}>No match performance data for the selected matches.</div>
        )}
      </Block>
      {performanceByMatch.map(m => (
        <Block key={m.sessionId}>
          <div style={{ fontSize: 10, fontWeight: 700, color: CLR.text, margin: '0 0 4px' }}>
            {m.opponent} — {fmtDate(m.date)}{m.position ? ` (vs ${m.position} teammates)` : ''}
          </div>
          <PerformanceVolumeTable rows={m.rows} />
          <PerformancePerMinTable rows={m.rows} />
        </Block>
      ))}

      {/* ── Section 4: Conclusies / Aanbevelingen ── */}
      <Block forceNewPage>
        <SectionHeader label="4. Conclusies / Aanbevelingen" />
        <ul style={{ margin: 0, paddingLeft: 18, fontSize: 10, lineHeight: 1.6, color: CLR.text }}>
          {conclusions.map((c, i) => <li key={i}>{c}</li>)}
        </ul>
      </Block>

      {/* ── Section 5: Eindscore Fysiek op dit moment — same page as Section 4 when it fits ── */}
      <Block>
        <SectionHeader label="5. Eindscore Fysiek op dit moment" />
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
          <span style={{ fontSize: 32, fontWeight: 800, color: CLR.accent }}>
            {physicalScore == null ? 'no data' : Math.round(physicalScore)}
          </span>
          {physicalScore != null && <span style={{ fontSize: 12, color: CLR.muted }}>/ 100 (season)</span>}
        </div>
        {quartile && (
          <div style={{ fontSize: 10, color: CLR.muted, marginTop: 4 }}>
            {quartile.quartileLabel === 'top quartile'
              ? `Top quartile of the squad — ${quartile.percentile}th percentile of ${quartile.squadSize} players compared.`
              : `${quartile.quartileLabel[0].toUpperCase()}${quartile.quartileLabel.slice(1)} of the squad — ${quartile.percentile}th percentile of ${quartile.squadSize} players compared.`}
          </div>
        )}
      </Block>
    </>
  );
}
